// 화면과 서버가 같이 쓰는 메시지 형식 값과 웹소켓 메시지 타입 (docs/architecture.md, v2 architecture-review 4.2). 이 폴더는 아무것도 가져오지 않는다
import type { Anim, PlaceId } from './ids';

export const REACTS = ['👍', '❤️', '😂', '😮', '😢', '🎉'];   // 채팅 반응 이모지 (react 메시지의 e)
export const MID_RE = /^[0-9a-f]{8}\.\d{1,9}$/;   // 메시지 번호 mid = 보낸 사람 id + '.' + 순번
// /turn이 TURN을 못 주거나 화면이 /turn을 못 받을 때 쓰는 STUN 목록
export const STUN = [{ urls: 'stun:stun.cloudflare.com:3478' }, { urls: 'stun:stun.l.google.com:19302' }];

// SFU·직접 연결이 나르는 영상 종류. 순서가 트랜시버 순서(audio, video, video)이다
export type Kind = 'mic' | 'cam' | 'share';
export const SFU_KINDS: Kind[] = ['mic', 'cam', 'share'];
export const GRANTED_KINDS: Kind[] = ['mic', 'share'];   // 발언권이 있는 학생이 올릴 수 있는 종류

// ================= 웹소켓 메시지 (타입만. 실행 중 검사는 서버 컨트롤러와 화면 NET_ON 처리 함수가 한다) =================
// 메시지는 JSON 객체이고 종류는 t 필드이다. 아래는 정상 클라이언트가 보내는 모양이다: 서버는 이 타입을 믿지 않고 필드마다 다시 검사한다.

// 교실 송출 공지: sid = SFU 세션, rx = SFU로 받는 중, 종류 = SFU 트랙 이름
export type Pub = { sid: string; rx?: 1 } & { [K in Kind]?: string };

// 접속한 사람 한 명 (welcome·joined). id = 이 접속의 번호(탭마다 다르다), pid = 사람 아이디(같은 브라우저의 탭은 같다. 사람 저장소가 안 되면 없다).
// room = 지금 있는 장소: 마을·건물 안 또는 모험 스테이지(`stage:1-1`, 좌표는 그 스테이지의 픽셀). null이면 어디에도 없다(들어오기 전, 지도·제목 화면). seat = 앉은 자리 번호(-1 서 있음), sh·hd = 화면 공유·손들기
export type PlayerCard = {
  id: string; pid?: string; name: string; look: number; room: PlaceId | null; x: number; y: number; face: 1 | -1; st: Anim;
  seat: number; sh: number; hd: number; gr?: 1; pub?: Pub;   // gr = 발언권(강사가 줬다)
};

// 위치 (화면 → 서버 pos와 서버 → 화면 pos가 같은 필드이다. 서버 → 화면에는 id가 붙는다). x·y = 발 사이 점(px), vx·vy = 속도(px/s, 앞질러 그리는 데 쓴다)
export type PosFields = { room: PlaceId; x: number; y: number; face: 1 | -1; st: Anim; vx: number; vy: number };

// 회의 상태 (앉은 자리·화면 공유·손들기). 위치와 따로 바뀔 때만 보낸다. seat = 자리 번호(-1 서 있음), share·hand = 0|1.
// 서버 규칙(server/2-controllers/meeting/classroom.ts): 화면 공유 칸은 모임마다 하나, 모임이 없으면 공유·손들기 불가, 모임이 바뀌면 둘 다 내려간다.
// 공유가 거절되거나 서버가 내리면 본인에게도 { t: 'meet', k: 'share', id: 내 id, v: 0 }이 온다(화면이 공유를 멈춘다)
export type MeetKind = 'seat' | 'share' | 'hand';

// 발언권 (shared/world/classroom.ts, docs 9장): 강사(교탁 자리)가 학생에게 주고 거둔다. 서버는 강사가 아니면 무시한다.
// 서버 → 화면: grant·revoke = 그 학생(id)의 발언권이 바뀌었다(by = 강사 이름, 강사가 떠나 거둘 때는 빈 문자열), clear = 강사가 없어져 모두 거뒀다
export type ClsKind = 'grant' | 'revoke';

export type SfuOp = 'new' | 'push' | 'pull' | 'reneg' | 'close' | 'pub';

// 화면 → 서버
export type ClientMsg =
  | { t: 'join'; name: string; look: number; token?: string }   // token = 브라우저가 저장한 사람 토큰
  | ({ t: 'pos' } & PosFields)
  | { t: 'away' }   // 장소(마을·건물·스테이지)를 떠난다(지도·제목 화면). 다음 pos가 오면 다시 나타난다
  | { t: 'ping' }
  | { t: 'chat'; text: string }
  | { t: 'img'; data: string; w: number; h: number }
  | { t: 'react'; mid: string; e: string }
  | { t: 'sig'; to: string; data: Record<string, unknown> }
  | { t: 'meet'; k: MeetKind; v: number }
  | { t: 'cls'; k: ClsKind; id: string }
  | { t: 'sfu'; q: number; op: SfuOp; body?: unknown };

// 서버 → 화면
export type ServerMsg =
  | { t: 'welcome'; id: string; pid?: string; token?: string; players: PlayerCard[] }   // pid = 사람 아이디, token = 새로 발급했을 때만 (저장해 두었다가 다음 join에 보낸다)
  | { t: 'joined'; p: PlayerCard }
  | { t: 'left'; id: string }
  | ({ t: 'pos'; id: string } & PosFields)
  | { t: 'away'; id: string }   // 그 사람이 장소를 떠났다: 자리·손들기도 풀린다
  | { t: 'meet'; k: MeetKind; id: string; v: number }   // 보낸 사람 빼고 방 전체. 늦게 들어온 사람은 welcome·joined의 카드(seat·sh·hd)로 안다
  | { t: 'full' }
  | { t: 'pong' }
  | { t: 'chat'; id: string; name: string; mid: string; text: string }
  | { t: 'img'; id: string; name: string; mid: string; data: string; w: number; h: number }
  | { t: 'react'; mid: string; e: string; id: string }
  | { t: 'sig'; from: string; data: Record<string, unknown> }
  | { t: 'cls'; k: ClsKind; id: string; by: string }
  | { t: 'cls'; k: 'clear' }
  | { t: 'pub'; id: string; pub: Pub | null }
  | { t: 'sfu'; q: number; ok: boolean; res?: unknown; err?: unknown };   // ok면 res, 아니면 err('class'·'rate'·'off'·'stop'·'bad'·'track'·'push'·'wait'·'api')

export type ClientT = ClientMsg['t'];
export type ServerT = ServerMsg['t'];
export type ClientOf<T extends ClientT> = Extract<ClientMsg, { t: T }>;
export type ServerOf<T extends ServerT> = Extract<ServerMsg, { t: T }>;
